import { useQuery } from "@tanstack/react-query";
import { fetchDashboardOverview } from "../../api/queries";
import { useLanguage } from "../../contexts/LanguageContext";
import { percentChange } from "../../utils/percentChange";
import ActivityFeed from "./components/ActivityFeed";
import GrowthChart from "./components/GrowthChart";
import InsightsTeaserCard from "./components/InsightsTeaserCard";
import KpiCard from "./components/KpiCard";
import PointsPanel from "./components/PointsPanel";
import { ChangePill, SectionError, Skeleton } from "./components/ui";
import { formatNumber } from "./format";

const KPIS = ["new_customers", "active_customers", "visits", "rewards_redeemed"];

export default function Overview({ storeId, range, tier, teaser }) {
  const { t, language } = useLanguage();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["dashboard", "overview", storeId, range],
    queryFn: () => fetchDashboardOverview(storeId, range),
    enabled: !!storeId,
  });

  if (isError) return <SectionError onRetry={() => refetch()} />;

  const loading = isLoading || !data;
  const rangeDays = data?.range_days ?? range;
  const kpis = data?.kpis || {};

  return (
    <div className="flex flex-col gap-6">
      <OverviewSummary data={data} loading={loading} rangeDays={rangeDays} />

      <section
        aria-label={t("dashboard.overview.kpisLabel")}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        {KPIS.map((key) => {
          const kpi = kpis[key];
          return (
            <KpiCard
              key={key}
              loading={loading}
              label={t(`dashboard.overview.kpis.${key}`)}
              value={formatNumber(kpi?.current ?? 0, language)}
              pill={<ChangePill change={percentChange(kpi?.current, kpi?.previous)} />}
              series={kpi?.series || []}
              footnote={t("dashboard.overview.previousPeriod", {
                count: rangeDays,
                value: formatNumber(kpi?.previous ?? 0, language),
              })}
            />
          );
        })}
      </section>

      <div className="flex flex-wrap items-stretch gap-4">
        <GrowthChart
          daily={data?.daily}
          dailyPrevious={data?.daily_previous}
          rangeDays={rangeDays}
          loading={loading}
        />
        <PointsPanel points={data?.points} rangeDays={rangeDays} loading={loading} />
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <ActivityFeed
          items={data?.recent_activity}
          loading={loading}
          className="flex-[2_1_560px]"
        />
        {tier === "basic" && (
          <InsightsTeaserCard
            atRisk={teaser?.insights?.at_risk}
            previewHref={`?tab=insights&range=${range}`}
            className="flex-[1_1_300px]"
          />
        )}
      </div>
    </div>
  );
}

function OverviewSummary({ data, loading, rangeDays }) {
  const { t, language } = useLanguage();
  if (loading) return <Skeleton className="h-5 w-full max-w-xl" />;

  const visits = percentChange(data.kpis?.visits?.current, data.kpis?.visits?.previous);
  let visitsSentence = "";
  if (visits?.type === "change" && visits.value !== 0) {
    visitsSentence = t(
      visits.value > 0 ? "dashboard.overview.summary.visitsUp" : "dashboard.overview.summary.visitsDown",
      { value: Math.abs(visits.value) },
    );
  }

  return (
    <p className="max-w-2xl text-[15px] leading-relaxed text-gray-700">
      {t("dashboard.overview.summary.main", {
        count: rangeDays,
        newCustomers: formatNumber(data.kpis?.new_customers?.current ?? 0, language),
        rewards: formatNumber(data.kpis?.rewards_redeemed?.current ?? 0, language),
        total: formatNumber(data.total_customers ?? 0, language),
      })}
      {visitsSentence && ` ${visitsSentence}`}
    </p>
  );
}
